import { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { apiSaveUser, apiSetUserTheme } from "../../services/UserService";
import {
  disableNotificationSetting,
  enableNotificationSetting,
  getUserNotificationSettings,
} from "../../services/NotificationService";
import Switch from "react-switch";
import { toastSuccess, toastFail } from "../../config";
import { useSelector, useDispatch } from "react-redux";
import { setUser, selectUser } from "../../redux/features/appData/appDataSlice";
import ReactSelect from "react-select";
import "./Settings.css";
import { useAuth } from "../../hooks/useAuth";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import config from "environment";
import { postImage } from "../../services/client";
import { validatePassword } from "../shared/uiHelpers";
import {
  AuthSource,
  apiAuthType,
  apiAuthChangePassword,
} from "../../services/AuthService";
import { useNavigate, useLocation } from "react-router-dom";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";

const showNotificationTab = true; // do we show notifications settings

const allowThemeChange = config.allowThemeChange;

const TABS_NUMBERS = {
  BASICS: "1",
  NOTIFICATIONS: "2",
  PASSWORD: "3",
  APPS: "4",
  SNIPPETS: "5",
};

const options = [
  { label: "Light Mode", value: "light-mode" },
  { label: "Dark Mode", value: "dark-mode" },
];

export default function Settings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader(true);

  const storeUser = useSelector(selectUser);

  const [logoFile, setLogoFile] = useState("");
  const logoFileRef = useRef();

  const [tabNumber, setTabNumber] = useState(TABS_NUMBERS.BASICS);

  const initialState = {
    user: null,
    changed: false,
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    title: "",
    notifications: true,
    theme: "",
  };

  const [settingsData, setSettingsData] = useState(initialState);

  const passState = {
    oldPass: "",
    newPass: "",
    checkPass: "",
  };

  const [passwordType, setPasswordType] = useState(null);
  const [passwordData, setPasswordData] = useState(passState);
  const [oldPasswordError, setOldPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");

  const notificationsInit = {
    onComment: true,
    commentNotificationId: undefined,
    onReply: true,
    replyNotificationId: undefined,
    onProgress: true,
    progressNotificationId: undefined,
    onMention: true,
    mentionAlertNotificationId: undefined,
    onAddedToWorkspace: true,
    addedToWorkspaceNotificationId: undefined,
  };

  const [notifications, setNotifications] = useState(notificationsInit);

  useEffect(() => {
    auth.checkStatus();

    // if we have a tab in query string use it
    const queryParams = new URLSearchParams(location.search);
    if (queryParams) {
      const tab = queryParams.get("tab");
      if (tab) {
        console.log("[Workspace] setting tab: ", tab);
        setTabNumber(tab);
      }
    }
  }, []);

  useEffect(() => {
    auth.checkStatus();

    if (!passwordType && storeUser) {
      (async () => {
        const pType = await apiAuthType(storeUser.email);

        // if it fails change it to GAUTH so the option doesn't appear
        setPasswordType(pType ? pType : AuthSource.GOOGLE_AUTH);
      })();
    }

    // if we have a tab in query string use it
    const queryParams = new URLSearchParams(location.search);
    if (queryParams) {
      const tab = queryParams.get("tab");
      if (tab) {
        console.log("[Settings] setting tab: ", tab);
        setTabNumber(tab);
      }
    }
  }, []);


  useEffect(() => {
    if (!storeUser) {
      return;
    }

    let user = storeUser;

    // if not all that stuff, then just prep the react states for form edits
    setSettingsData({
      ...settingsData,
      user,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      notifications: user.notifications,
      theme: user.theme,
    });

    (async () => {
      // load the notifications from the DB
      const notificationSettings = await getUserNotificationSettings(
        storeUser.id
      );

      if (notificationSettings) {
        const findEmailNotificationIndex = (notificationType) => {
          return notificationSettings.findIndex((setting) => {
            return (
              setting.adapterType === "EMAIL" &&
              setting.notificationType === notificationType
            );
          });
        };

        // we get only the email ones right now as that's what we are dealing with
        const commentIndex = findEmailNotificationIndex(
          "COMMENT_ON_YOUR_VIDEO"
        );

        const replyIndex = findEmailNotificationIndex("REPLY_TO_YOUR_COMMENT");

        const progressIndex = findEmailNotificationIndex("JIRA_UPDATE");

        const mentionIndex = findEmailNotificationIndex("MENTION_ALERT");

        const addedToWorkspaceIndex =
          findEmailNotificationIndex("ADDED_TO_WORKSPACE");

        setNotifications({
          onComment:
            commentIndex === -1
              ? false
              : notificationSettings[commentIndex].enabled,
          commentNotificationId:
            commentIndex === -1
              ? undefined
              : notificationSettings[commentIndex].id,
          onReply:
            replyIndex === -1
              ? false
              : notificationSettings[replyIndex].enabled,
          replyNotificationId:
            replyIndex === -1 ? undefined : notificationSettings[replyIndex].id,
          onProgress:
            progressIndex === -1
              ? false
              : notificationSettings[progressIndex].enabled,
          progressNotificationId:
            progressIndex === -1
              ? undefined
              : notificationSettings[progressIndex].id,
          onMention:
            mentionIndex === -1
              ? false
              : notificationSettings[mentionIndex].enabled,
          mentionAlertNotificationId:
            mentionIndex === -1
              ? undefined
              : notificationSettings[mentionIndex].id,
          onAddedToWorkspace:
            addedToWorkspaceIndex === -1
              ? false
              : notificationSettings[addedToWorkspaceIndex].enabled,
          addedToWorkspaceNotificationId:
            addedToWorkspaceIndex === -1
              ? undefined
              : notificationSettings[addedToWorkspaceIndex].id,
        });
      }
    })();
  }, [storeUser]);

  const handleSave = () => {
    (async () => {
      const newUser = {
        ...settingsData.user,
        firstName: settingsData.firstName,
        lastName: settingsData.lastName,
        notifications: settingsData.notifications,
      };

      const apiUser = await apiSaveUser(newUser);

      if (!apiUser) {
        toast("Could not save user, please try again", toastFail);
        return;
      }

      toast("Saved", toastSuccess);
      setSettingsData({ ...settingsData, changed: false });

      // reset store users
      dispatch(setUser(apiUser));
    })();
  };

  const handleSwitchChange = async (field) => {
    const current = notifications[field];

    const newNotifications = { ...notifications, [field]: !current };

    setNotifications(newNotifications);

    // update the DB dynamically
    switch (field) {
      case "onComment":
        if (newNotifications.commentNotificationId !== undefined) {
          newNotifications.onComment
            ? await enableNotificationSetting(
                newNotifications.commentNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.commentNotificationId
              );
        }
        break;
      case "onReply":
        if (newNotifications.replyNotificationId !== undefined) {
          newNotifications.onReply
            ? await enableNotificationSetting(
                newNotifications.replyNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.replyNotificationId
              );
        }
        break;
      case "onProgress":
        if (newNotifications.progressNotificationId !== undefined) {
          newNotifications.onProgress
            ? await enableNotificationSetting(
                newNotifications.progressNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.progressNotificationId
              );
        }
        break;
      case "onMention":
        if (newNotifications.mentionAlertNotificationId !== undefined) {
          newNotifications.onMention
            ? await enableNotificationSetting(
                newNotifications.mentionAlertNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.mentionAlertNotificationId
              );
        }
        break;
      case "onAddedToWorkspace":
        if (newNotifications.addedToWorkspaceNotificationId !== undefined) {
          newNotifications.onAddedToWorkspace
            ? await enableNotificationSetting(
                newNotifications.addedToWorkspaceNotificationId
              )
            : await disableNotificationSetting(
                newNotifications.addedToWorkspaceNotificationId
              );
        }
        break;
      default:
        break;
    }
  };

  const changeTab = (e, newValue) => {
    setTabNumber(newValue);
  };

  const handleThemeChange = async (input, action) => {
    await apiSetUserTheme(storeUser.id, input.value);
    dispatch(setUser({ ...storeUser, theme: input.value }));
    document.body.className = input.value;
  };

  const changeLogo = () => {
    if (logoFileRef?.current) {
      const inputButton = logoFileRef.current;
      setLogoFile("");
      inputButton.click();
    }
  };

  const handleFileChange = (event) => {
    if (!storeUser || !storeUser.currentWorkspaceId) {
      return;
    }

    if (event.target.files && event.target.files?.length < 1) {
      return;
    }

    if (event.target.files && event.target.files[0].size > 10485760) {
      toast("Avatar is too big! Please keep it under 10MB", toastFail);
      return;
    }

    const theFile = event.target.files ? event.target.files[0] : null;

    if (!theFile) {
      toast("Did not get a valid file", toastFail);
      return;
    }

    (async () => {
      toast("Uploading...", toastSuccess);

      try {
        const response = await postImage(storeUser.currentWorkspaceId, theFile, null);

        if (response.status === 200) {
          const theUser = {
            ...settingsData.user,
            avatarURL: response.data.data,
          };
          
          setSettingsData({ ...settingsData, user: theUser });

          const apiUser = await apiSaveUser(theUser);

          if (!apiUser) {
            toast("Could not save user, please try again.", toastFail);
            return;
          }

          dispatch(setUser(apiUser));
          reloadAll(storeUser?.currentWorkspaceId);
        } else {
          toast("File not uploaded: " + response.status, toastFail);
        }
      } catch (error) {
        // Handle error
        toast("Error uploading file, please try again.", toastFail);
      }
    })();
  };

  // expects valid passwords (e.g. format is valid)
  const handleChangePassword = async () => {
    setNewPasswordError("");
    setOldPasswordError(false);

    if (passwordData.newPass !== passwordData.checkPass) {
      setNewPasswordError("New passwords do not match.");
      return;
    }

    if (passwordData.oldPass === passwordData.newPass) {
      setNewPasswordError("Please use a different password.");
      return;
    }

    const result = await apiAuthChangePassword(
      storeUser.email,
      passwordData.oldPass,
      passwordData.newPass
    );

    if (!result) {
      // setNewPasswordError(true);
      toast("Password update failed.", toastFail);
      return;
    }

    toast("Password updated.", toastSuccess);
  };

  const { user, theme } = settingsData;

  const index = options.findIndex((option) => option.value === theme);
  const defaultValue = index !== -1 ? index : 0;

  const pickerOptions = {
    // DropdownIndicator: false,
    // Input: false,
    IndicatorSeparator: () => null,
  };

  // console.log("[Settings/main] user: ", storeUser);
  // console.log("[Settings/main] notifications: ", notifications);

  // don't check new and old match as we want to show this in handler
  const passwordsMatch =
    validatePassword(passwordData.oldPass) &&
    validatePassword(passwordData.newPass) &&
    validatePassword(passwordData.checkPass);

  console.log("[Settings] Allow theme change: ", allowThemeChange);

  return (
    <>
      <div id="settings">
        <div className="page-title">
          Personal Settings
        </div>

        {user && (
          <div className="settings-body">
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={tabNumber}>
                <Box sx={{}}>
                  <TabList
                    onChange={changeTab}
                    aria-label="lab API tabs example"
                    TabIndicatorProps={{
                      style: { background: "var(--text-color-primary)" },
                    }}>
                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Basic Details"
                      value="1"
                    />
                    {passwordType === AuthSource.STANDARD && (
                      <Tab
                        sx={{
                          "&.Mui-selected": {
                            color: "var(--text-color-primary)",
                          },
                          color: "var(--text-color-secondary)",
                        }}
                        label="Password"
                        value="3"
                      />
                    )}
                    {/* {showNotificationTab && (
                      <Tab
                        sx={{
                          "&.Mui-selected": {
                            color: "var(--text-color-primary)",
                          },
                          color: "var(--text-color-secondary)",
                        }}
                        label="Notifications"
                        value="2"
                      />
                    )} */}
                    
                  </TabList>
                </Box>

                <TabPanel value="1">
                  <div className="settings-personal-info">
                    {allowThemeChange && (
                      <>
                        {/* <div className="settings-personal-divider">
                        </div> */}

                        <div className="settings-personal-theme">
                          <div className="settings-personal-theme-label">
                            <h2>Pick your theme</h2>
                            <span
                              style={{ color: "var(--text-color-secondary)" }}>
                              Choose wisely! So many choices.
                            </span>
                          </div>
                          <div className="settings-personal-theme-select">
                            <ReactSelect
                              options={options}
                              defaultValue={options[defaultValue]}
                              components={pickerOptions}
                              isSearchable={false}
                              onChange={handleThemeChange}
                              styles={{
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isFocused
                                    ? "var(--tag-background-color)"
                                    : state.isSelected
                                      ? "var(--background-color-secondary)"
                                      : "var(--background-color-primary)",
                                  color: "var(--text-color-primary)",
                                  cursor: "pointer",
                                }),
                                menu: (base) => ({
                                  ...base,
                                  backgroundColor:
                                    "var(--background-color-primary)",
                                }),
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="settings-personal-header">
                      <h2> Personal info</h2>
                      <span style={{ color: "var(--text-color-secondary)" }}>
                        Change the way you represent yourself to your team and
                        others.
                      </span>
                    </div>
                    <div className="settings-personal-avatar">
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/gif"
                        ref={logoFileRef}
                        style={{ display: "none" }}
                        value={logoFile}
                        onChange={handleFileChange}
                        onError={(event) => console.log("onError", event)}
                      />
                      <img src={user.avatarURL} />
                      <button 
                        // id="button-upload" 
                        className="button-save"
                        onClick={changeLogo}
                      >
                        Upload Photo
                      </button>
                    </div>
                    <div className="settings-personal-name">
                      <div className="settings-personal-name-part">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={settingsData.firstName}
                          onChange={(e) =>
                            setSettingsData({
                              ...settingsData,
                              changed: true,
                              firstName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="settings-personal-name-part">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={settingsData.lastName}
                          onChange={(e) =>
                            setSettingsData({
                              ...settingsData,
                              changed: true,
                              lastName: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="settings-personal-email">
                      <label>Email</label>
                      <input disabled type="text" value={settingsData.email} />
                    </div>
                    <div className="settings-personal-save">
                      <button
                        id="button-save"
                        disabled={!settingsData.changed}
                        onClick={handleSave}>
                        Save
                      </button>
                    </div>
                  </div>
                </TabPanel>

                {passwordType === AuthSource.STANDARD && (
                  <TabPanel value="3">
                    <div className="settings-personal-password">
                      <div className="settings-personal-password-header">
                        <h2>Update your password</h2>
                        <span style={{ color: "var(--text-color-secondary)" }}>
                          Password must contain 8 characters, 1 capital letter
                          and 1 number.
                        </span>
                      </div>
                    </div>

                    <div className="settings-personal-inputs">
                      <div className="settings-personal-input">
                        <label>Old Password</label>
                        <input
                          type="password"
                          placeholder="Enter your existing password."
                          value={passwordData.oldPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              oldPass: e.target.value,
                            })
                          }
                        />
                        <span
                          hidden={!oldPasswordError}
                          style={{ color: "var(--text-color-error)" }}>
                          Existing password does not match.
                        </span>
                      </div>

                      <div className="settings-personal-input">
                        <label>New Password</label>
                        <input
                          type="password"
                          placeholder="Enter a new password."
                          value={passwordData.newPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPass: e.target.value,
                            })
                          }
                        />
                        <label>Confirm New Password</label>
                        <input
                          type="password"
                          placeholder="Enter new password again."
                          value={passwordData.checkPass}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              checkPass: e.target.value,
                            })
                          }
                        />
                        <span
                          hidden={!newPasswordError}
                          style={{ color: "var(--text-color-error)" }}>
                          New passwords do not match.
                        </span>
                        <div className="personal-password-submit">
                          <button
                            className="button-save"
                            onClick={handleChangePassword}
                            disabled={!passwordsMatch}>
                            Change Password
                          </button>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                )}

                {showNotificationTab && (
                  <>
                    <TabPanel value="2">
                      <div className="settings-personal-notifications">
                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when someone comments on my video
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onComment")}
                              checked={notifications.onComment}
                              // style={{background: "var(--button-color-secondary)"}}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when someone replies to my comment
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onReply")}
                              checked={notifications.onReply}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>Notify me when someone mentions me</label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() => handleSwitchChange("onMention")}
                              checked={notifications.onMention}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                        <div className="settings-personal-notify-container">
                          <div className="settings-personal-notify-label">
                            <label>
                              Notify me when I am added to a new workspace
                            </label>
                          </div>
                          <div className="settings-personal-notify-switch">
                            <Switch
                              onChange={() =>
                                handleSwitchChange("onAddedToWorkspace")
                              }
                              checked={notifications.onAddedToWorkspace}
                              onColor="#1665ff"
                              // onColor="var(--button-color-primary)"
                            />
                          </div>
                        </div>

                      </div>
                    </TabPanel>
                  </>
                )}

              </TabContext>
            </Box>
          </div>
        )}
      </div>
      
    </>
  );
}
